import {type PlayClass} from "./generic/PlayClass";
import {CLASS_TYPE_DISPLAY_VALUES, type ClassType} from "../types/ClassType";
import {type AbstractPlayableContent} from "../../../playable/models/AbstractPlayableContent";
import {defaultVersion} from "../../../playable/models/Version";
import {playablePool} from "../../../playable/utils/PlayablePool";
import {createClassFromJson} from "../../utils/PlayClassFactory";

export type RootClassContent = {
  // False negative here
  // eslint-disable-next-line no-unused-vars
  [ActiveType in ClassType]?: PlayClass|undefined;
}
export type RootClassContentIsFinished = {
  // False negative here
  // eslint-disable-next-line no-unused-vars
  [ActiveType in ClassType]?: boolean|undefined;
}

export class RootClass {
  public readonly playableContent: AbstractPlayableContent;
  public content: RootClassContent = {
    'PIANO_BOTH': undefined,
  };

  get isFinished(): boolean {
    return Object.values(this.content).every((playClass) => playClass?.progress.isFinished)
  }
  get isStarted(): boolean {
    return (this.content.PIANO_BOTH?.progress.total || 0) > 0;
  }
  get eachContentIsFinished(): RootClassContentIsFinished {
    const rootClassContentIsFinished = {} as RootClassContentIsFinished;
    Object.keys(this.content).forEach((key) => {
      rootClassContentIsFinished[key as ClassType] = this.content[key as ClassType]?.progress.isFinished
    })
    return rootClassContentIsFinished;
  }

  constructor(playableContent: AbstractPlayableContent) {
    this.playableContent = playableContent
  }

  public set(type: ClassType, playClass: PlayClass) {
    switch (type) {
      case "PIANO_BOTH":
      case "SING":
      case "CONSOLIDATION":
        if (this.content[type] !== undefined) {
          console.error(`${type} is already set in root class.`);
        }
        else {
          this.content[type] = playClass;
        }
        break;

      default:
        console.error(`${type} not supported in root class.`);
        break;
    }
  }

  public getProgressLabel(type: ClassType) {
    if (type === 'PIANO_BOTH') {
      return `${CLASS_TYPE_DISPLAY_VALUES[type]} (${Math.round((this.content.PIANO_BOTH!.progress.total * 0.8 + this.content.CONSOLIDATION!.progress.total * 0.2) * 100) + '%'})`
    }
    return `${CLASS_TYPE_DISPLAY_VALUES[type]} (${this.content[type]!.progress.displayValue})`
  }

  public getProgress(type: ClassType) {
    if (type === 'PIANO_BOTH') {
      return Math.round((this.content.PIANO_BOTH!.progress.total * 0.8 + this.content.CONSOLIDATION!.progress.total * 0.2) * 100)
    }
    return this.content[type]!.progress.total
  }

  public getClassName(type: ClassType) {
    return `${CLASS_TYPE_DISPLAY_VALUES[type]}`
  }

  static createFromJson(versionId: number, json: any) {
    const rootClass = new RootClass(playablePool.get('version', versionId)!);

    rootClass.set('PIANO_BOTH', createClassFromJson(json.classes.find((jsonClass: any) => jsonClass.type === 'PIANO_BOTH')))
    rootClass.set('CONSOLIDATION', createClassFromJson(json.classes.find((jsonClass: any) => jsonClass.type === 'CONSOLIDATION')))
    if (json.classes.find((jsonClass: any) => jsonClass.type === 'SING')) {
      rootClass.set('SING', createClassFromJson(json.classes.find((jsonClass: any) => jsonClass.type === 'SING')))
    }

    return rootClass;
  }
}

export const defaultRootClass = new RootClass(defaultVersion);
